import { Form, Modal } from "antd";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { deepEqual } from "../../utils/misc";

import { ApiNames } from "../../api/api";
import { ReactComponent as WarningIcon } from "../../assets/svg/warning-icon-popup.svg";
import ACTIONS from "../../store/action";
import {
  getUserSelectedColumns,
  selectFilterList,
  selectFilters,
  selectFormPopups,
  selectIsFetching,
  selectSorting
} from "../../store/selector";
import "../FormPopupsModal.css";
import {
  selectAssigneesAndUserGroups,
  selectAllUsersAndUserGroups,
  selectUserGroupsWithUsers,
  selectUser
} from "../../store/selector";
import "../FormPopupsModal.css";
import SavedListComponent from "@tailormed/common-client/component/SavedList/SavedListComponent.js";
import { LIST_TYPES } from "@tailormed/common-client/constant/savedList";
import { parseViewers } from "@tailormed/common-client/util/savedList";

const AddFilterListFormComponent = ({ setFormPopupsAct }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { filterListId } = useParams();

  const filters = useSelector(selectFilters);
  const filterList = useSelector(selectFilterList);
  const isFetching = useSelector(selectIsFetching([ApiNames.CreateFilterList, ApiNames.UpdateFilterList]));
  const userSelectedColumns = useSelector(getUserSelectedColumns);
  const formPopups = useSelector(selectFormPopups);
  const sorting = useSelector(selectSorting);
  const updateFilterListAct = (newFilterListData, filterListId) =>
    dispatch(ACTIONS.updateFilterListAction(newFilterListData, filterListId));
  const setFilterListAct = (filterListData) =>
    dispatch(ACTIONS.setFilterList({ ...filterListData, isEditView: false }));
  const createFilterListAct = (filterListData) => dispatch(ACTIONS.createFilterListAction(filterListData));
  const { visible, isDuplicateMode } = formPopups;
  const [modal, contextHolder] = Modal.useModal();
  const assigneesAndGroups = useSelector(selectAssigneesAndUserGroups) || []; // For dropdown (assignable only)
  const allUsersAndGroups = useSelector(selectAllUsersAndUserGroups) || []; // For displaying existing viewers
  const userGroupsWithUsers = useSelector(selectUserGroupsWithUsers);
  const currentUser = useSelector(selectUser);

  const handleAddButton = (formData) => {
    let finalViewers = formData.viewers?.map((viewer) => viewer.value) || [];

    if (isDuplicateMode && formData.type === LIST_TYPES.SHARED && currentUser?.id !== filterList?.user) {
      const originalOwnerId = filterList.user;
      const isOwnerAlreadyInViewers = finalViewers.some((viewerId) => viewerId === originalOwnerId);

      if (!isOwnerAlreadyInViewers) {
        // Use allUsersAndGroups to find original owner (might be unassignable)
        const originalOwner = allUsersAndGroups.find((item) => item.id === originalOwnerId);

        if (originalOwner) {
          finalViewers = [...finalViewers, originalOwner.id];
        }
      }
    }

    const filterListValues = {
      name: formData.name.trim(),
      description: formData.description?.trim(),
      type: formData.type || LIST_TYPES.PRIVATE,
      params: {
        filters: {
          ...filters,
          isOpen: false
        },
        selectedColumns: userSelectedColumns,
        sorting
      },
      selectedView: t("new_list_form.filter_list_selected_view"),
      viewers: finalViewers
    };
    filterList.isEditView
      ? updateFilterListAct(filterListValues, filterList?.id)
      : createFilterListAct(filterListValues);
  };

  const handleCancelButton = (formData) => {
    // If formData is null, it means there are no changes, so just close
    if (!formData) {
      cleanForm();
      setFormPopupsAct(false);
      return;
    }

    const filterListObject = {
      description: filterList.description || "",
      name: filterList.name || "",
      type: filterList.type || LIST_TYPES.PRIVATE,
      viewers: parseViewers(filterList.viewers)
    };

    const currentFormData = {
      description: formData?.description || "",
      name: formData?.name || "",
      type: formData?.type || LIST_TYPES.PRIVATE,
      viewers: formData?.viewers?.map((viewer) => viewer.value) || []
    };

    // Check for changes in form data OR if there are unsaved changes (including pending visibility change)
    const hasChanges = !deepEqual(currentFormData, filterListObject) || formData?.hasUnsavedChanges;

    hasChanges
      ? modal.confirm({
          className: "formPopupsModal",
          centered: true,
          width: 520,
          okText: t("yes_sure"),
          cancelText: t("back"),
          icon: <WarningIcon />,
          title: t("new_list_form.unsaved_details"),
          content: t("new_list_form.not_saved_yet"),
          onOk() {
            cleanForm(), setFormPopupsAct(false);
          }
        })
      : (cleanForm(), setFormPopupsAct(false));
  };

  const cleanForm = () => {
    form.resetFields();
    if (!filterListId) setFilterListAct({});
    setFilterListAct({ ...filterList, isEditView: false });
  };

  return (
    <>
      <SavedListComponent
        visible={visible}
        initialData={filterList}
        isEditMode={filterList.isEditView}
        isDuplicateMode={isDuplicateMode}
        handleCancel={handleCancelButton}
        handleSave={handleAddButton}
        isLoading={isFetching}
        assigneesAndGroups={assigneesAndGroups}
        allUsersAndGroups={allUsersAndGroups}
        userGroupsWithUsers={userGroupsWithUsers}
        owner={filterList?.user || null}
        currentUser={currentUser}
        source="pharmacy"
      />
      {contextHolder}
    </>
  );
};

const FilterListForm = AddFilterListFormComponent;

export { FilterListForm };
